import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp,
  Activity,
  Server,
  Database,
  AlertCircle,
  CheckCircle,
  BarChart3,
  RefreshCw,
  Pause,
  Play
} from 'lucide-react';
import api from '../../services/api';

const SMPPMetricsDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    fetchMetrics();
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchMetrics, 3000);
      countdownRef.current = setInterval(() => setCountdown(prev => prev <= 1 ? 3 : prev - 1), 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [autoRefresh]);

  const fetchMetrics = async () => {
    try {
      const response = await api.get('/super/smpp/metrics');
      setMetrics(response.data);
      setLastUpdated(new Date());
      setCountdown(3);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            SMPP Metrics Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">Real-time performance metrics and system health</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
            {autoRefresh && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
            <span className="text-xs text-gray-500 font-mono">
              {autoRefresh ? `Next: ${countdown}s` : 'Paused'}
            </span>
            {lastUpdated && <span className="text-xs text-gray-400">| {lastUpdated.toLocaleTimeString()}</span>}
          </div>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              autoRefresh ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
            }`}
          >
            {autoRefresh ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {autoRefresh ? 'Pause' : 'Resume'}
          </button>
          <button
            onClick={fetchMetrics}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 text-xs font-medium transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {metrics && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium opacity-90">Inbound TPS</h3>
                <TrendingUp className="w-6 h-6 opacity-80" />
              </div>
              <p className="text-4xl font-bold mb-2">{metrics.current_tps_inbound}</p>
              <p className="text-sm opacity-80">Messages per second</p>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium opacity-90">Outbound TPS</h3>
                <TrendingUp className="w-6 h-6 opacity-80" />
              </div>
              <p className="text-4xl font-bold mb-2">{metrics.current_tps_outbound}</p>
              <p className="text-sm opacity-80">Messages per second</p>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium opacity-90">Active Connections</h3>
                <Server className="w-6 h-6 opacity-80" />
              </div>
              <p className="text-4xl font-bold mb-2">{metrics.active_connections}</p>
              <p className="text-sm opacity-80">Connected clients</p>
            </div>

            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium opacity-90">Error Rate</h3>
                <AlertCircle className="w-6 h-6 opacity-80" />
              </div>
              <p className="text-4xl font-bold mb-2">
                {metrics.error_rate?.error_rate_percent?.toFixed(2) || 0}%
              </p>
              <p className="text-sm opacity-80">Last hour</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-600" />
                  Queue Depth
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Kafka Inbound</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {metrics.queue_depth?.kafka_inbound || 0}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${Math.min((metrics.queue_depth?.kafka_inbound || 0) / 1000 * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Kafka Outbound</span>
                      <span className="text-2xl font-bold text-green-600">
                        {metrics.queue_depth?.kafka_outbound || 0}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${Math.min((metrics.queue_depth?.kafka_outbound || 0) / 1000 * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Kafka DLR</span>
                      <span className="text-2xl font-bold text-purple-600">
                        {metrics.queue_depth?.kafka_dlr || 0}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full"
                        style={{ width: `${Math.min((metrics.queue_depth?.kafka_dlr || 0) / 1000 * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-red-600" />
                  Error Metrics
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Submit Errors</p>
                        <p className="text-xs text-gray-500 mt-1">Failed submissions</p>
                      </div>
                      <span className="text-3xl font-bold text-red-600">
                        {metrics.error_rate?.submit_errors || 0}
                      </span>
                    </div>
                  </div>

                  <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Bind Failures</p>
                        <p className="text-xs text-gray-500 mt-1">Connection failures</p>
                      </div>
                      <span className="text-3xl font-bold text-yellow-600">
                        {metrics.error_rate?.bind_failures || 0}
                      </span>
                    </div>
                  </div>

                  <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Throttle Events</p>
                        <p className="text-xs text-gray-500 mt-1">Rate limit hits</p>
                      </div>
                      <span className="text-3xl font-bold text-orange-600">
                        {metrics.error_rate?.throttle_events || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-600" />
                Redis Metrics
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-3">
                    <Database className="w-8 h-8 text-blue-600" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {metrics.redis_counters?.total_keys || 0}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Total Keys</p>
                </div>

                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-3">
                    <Activity className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {metrics.redis_counters?.memory_used_mb || 0} MB
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Memory Used</p>
                </div>

                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-3">
                    <Server className="w-8 h-8 text-purple-600" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {metrics.redis_counters?.connected_clients || 0}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Connected Clients</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-green-900 mb-1">
                  Real-Time Monitoring
                </h4>
                <p className="text-sm text-green-800">
                  All metrics are updated every 3 seconds. This dashboard provides comprehensive visibility
                  into SMPP platform performance, queue depths, error rates, and system health.
                  Last updated: {new Date(metrics.timestamp).toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SMPPMetricsDashboard;
